<template>
    <div class="d-form">
        <div
            v-for="item in formFieldsData"
            :key="item.name"
            :class="item.class"
        >
            <div v-if="item.name === 'scale'">
                <x-checkbox
                    name="manualScale"
                    id="manualScale"
                    :text="$t('labelManualScale')"
                    v-model="manualScale"
                />
            </div>
            <div v-if="item.type === 'form'">
                <PrintSettingsForm
                    :formData="item.elements"
                    @update="$_updatedForm(item, $event)"
                    @formError="$_updatedFormError(item, $event)"
                />
            </div>

            <x-input
                v-else-if="item.type === 'text'"
                :type="item.fieldType || item.type"
                :id="item.id"
                :name="item.id"
                :text="item.text"
                :placeholder="item.omschrijving"
                :required="item.required"
                :disabled="item.disabled"
                :state="item.valid"
                :invalidMessage="item.invalidMessage"
                :maxlength="item.maxlength"
                :value="item.value"
                v-model="item.value"
                @input="$_updatedValue(item.name)"
            >
            </x-input>
            <x-number
                v-else-if="item.type === 'number'"
                :id="item.id"
                :name="item.id"
                :text="item.text"
                :placeholder="item.omschrijving"
                :required="item.required"
                :disabled="item.disabled"
                :state="item.valid"
                :invalidMessage="item.invalidMessage"
                :minValue="item.minValue || 0"
                :maxValue="item.maxValue || 1000"
                :step="item.step || 1"
                :value="item.value"
                v-model="item.value"
                @input="$_updatedValue(item.name)"
            >
            </x-number>
            <x-number
                v-else-if="item.type === 'decimal-number'"
                :id="item.id"
                :name="item.id"
                :text="item.text"
                :placeholder="item.omschrijving"
                :required="item.required"
                :disabled="item.disabled"
                :state="item.valid"
                :invalidMessage="item.invalidMessage"
                :allowDecimals="true"
                :minValue="item.minValue || 0"
                :maxValue="item.maxValue || 1000"
                :step="item.step || 1"
                :value="item.value"
                :defaultValue="item.defaultValue"
                v-model="item.value"
                @input="$_updatedValue(item.name)"
            >
            </x-number>
            <x-textarea
                v-else-if="item.type === 'textarea'"
                :id="item.id"
                :name="item.id"
                :text="item.text"
                :placeholder="item.omschrijving"
                :rows="2"
                :required="item.required"
                :disabled="item.disabled"
                :state="item.valid"
                :invalidMessage="item.invalidMessage"
                :maxlength="item.maxlength"
                :value="item.value"
                v-model="item.value"
                @input="$_updatedValue(item.name)"
            >
            </x-textarea>
            <template v-else-if="item.type === 'select'">
                <template v-if="item.name !== 'scale' || !manualScale">
                    <x-select
                        :id="item.id"
                        :value="item.value"
                        :text="item.text"
                        :options="item.options"
                        :disabled="item.disabled"
                        :simpleMode="true"
                        :state="item.valid"
                        :invalidMessage="item.invalidMessage"
                        :showDefaultOption="item.showDefaultOption || false"
                        @selected="$_updatedSelect(item, $event)"
                    ></x-select>
                </template>
                <template v-else>
                    <x-number
                        :id="item.id"
                        :name="item.id"
                        :text="item.text"
                        :placeholder="item.omschrijving"
                        :required="item.required"
                        :disabled="item.disabled"
                        :state="item.valid"
                        :invalidMessage="item.invalidMessage"
                        :allowDecimals="true"
                        :minValue="item.minValue || 100"
                        :maxValue="item.maxValue || 1000000"
                        :step="item.step || 1"
                        :value="item.value"
                        :defaultValue="item.defaultValue"
                        v-model="item.value"
                        @input="$_updatedValue(item.name)"
                    >
                    </x-number>
                </template>
            </template>
            <x-checkbox
                v-else-if="item.type === 'checkbox'"
                :name="item.id"
                :id="item.id"
                :text="item.text"
                :disabled="item.disabled"
                v-model="item.value"
                @change="$_updatedSelect(item, $event)"
            >
            </x-checkbox>
            <b-form-group v-else-if="item.type === 'color'" :label="item.text">
                <color-picker
                    :id="item.id"
                    :name="item.id"
                    v-model="item.value"
                    @input="$_updatedSelect(item, $event)"
                >
                </color-picker>
            </b-form-group>
            <b-form-group v-else-if="item.type === 'radio'" :label="item.text">
                <b-form-radio-group
                    :id="item.id"
                    :name="item.id"
                    :options="item.options"
                    v-model="item.value"
                    @change="$_updatedSelect(item, $event)"
                >
                </b-form-radio-group>
            </b-form-group>
        </div>
    </div>
</template>

<script>
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import xinput from '../../components/BaseComponents/x-input.vue';
    import xnumber from '../../components/BaseComponents/x-number.vue';
    import XTextarea from '../../components/BaseComponents/x-textarea.vue';
    import ColorPicker from '../../components/ColorPicker.vue';

    export default {
        name: 'PrintSettingsForm',
        components: {
            'x-select': XSelect,
            'x-checkbox': XCheckbox,
            'x-input': xinput,
            'x-number': xnumber,
            'x-textarea': XTextarea,
            'color-picker': ColorPicker
        },
        props: { formData: { type: Array, required: true } },
        data() {
            return { formFieldsData: null, manualScale: false };
        },
        methods: {
            $_updatedFormError(formObject) {
                formObject.valid = false;
            },
            $_updatedForm(formObject, data) {
                formObject.valid = true;
                formObject.value = data;
                this.$_updatedValue();
            },
            $_updatedSelect(formObject, value) {
                // prevent string to number error when switching to manual scale input
                // store the select list selected value as a number
                if (formObject.name === 'scale') {
                    formObject.value = Number(value);
                } else {
                    formObject.value = value;
                }
                this.$_updatedValue(formObject.name);
            },
            $_updatedValue() {
                const exposedValues = {};
                this.validate();
                this.formFieldsData.forEach((formfield) => {
                    exposedValues[formfield.name] = formfield.value;
                });
                if (this.valid) {
                    this.$emit('update', exposedValues);
                } else {
                    this.$emit('formError');
                }
            },
            $_validateTextInputValue(option, input) {
                option.state = input.length > 0 && input.length <= option.maxlength;
            },
            $_validateTextField(formfield) {
                return formfield.value?.length > 0;
            },
            $_validateBooleanField(formfield) {
                return typeof formfield.value === 'boolean';
            },
            $_validateNumberField(formfield) {
                if (typeof formfield.value !== 'number') {
                    return false;
                }
                if (
                    typeof formfield.minValue === 'number' &&
                    formfield.minValue > formfield.value
                ) {
                    return false;
                }
                return typeof formfield.maxValue === 'number' &&
                    formfield.maxValue < formfield.value
                    ? false
                    : true;
            },
            validate() {
                this.formFieldsData.forEach((formfield) => {
                    formfield.valid = !formfield.required;
                    if (formfield.required) {
                        if (formfield.type === 'number') {
                            formfield.valid = this.$_validateNumberField(formfield);
                        } else if (formfield.type === 'text') {
                            formfield.valid = this.$_validateTextField(formfield);
                        } else if (formfield.type === 'checkbox') {
                            formfield.valid =
                                this.$_validateBooleanField(formfield);
                        } else {
                            formfield.valid =
                                typeof formfield.value === 'number' ||
                                formfield.value?.length > 0;
                        }
                    }
                });
            }
        },
        watch: {
            formData: {
                handler: function (val) {
                    this.formFieldsData = JSON.parse(JSON.stringify(this.formData));
                },
                immediate: true
            }
        },
        computed: {
            valid: function () {
                return !this.formFieldsData.find(({ valid }) => valid === false);
            }
        }
    };
</script>
